<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ArrowRight, Ban, Plus, RefreshCw, Trash2, X } from 'lucide-vue-next'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useAuthStore } from '@yuksalish/auth'
import { useProcurementApi } from '../composables/useProcurementApi'
import ListPagination from '../components/ListPagination.vue'
import {
  SUPPLIER_INVOICE_STATUSES,
  isOpenSupplierInvoice,
  isOverdueInvoice,
  supplierInvoiceStatusClass,
} from '../types/procurement'
import type { AgentOption, SaveSupplierInvoicePayload, SupplierInvoice } from '../types/procurement'

const { t } = useI18n()
const auth = useAuthStore()
const api = useProcurementApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canCreate = computed(() => permissions.value.includes('supplier_invoices.create'))
const canUpdate = computed(() => permissions.value.includes('supplier_invoices.update'))
const canDelete = computed(() => permissions.value.includes('supplier_invoices.delete'))

const invoices = ref<SupplierInvoice[]>([])
const suppliers = ref<AgentOption[]>([])
const loading = ref(true)
const error = ref('')

const search = ref('')
const statusFilter = ref('')
const supplierFilter = ref('')
const currencyFilter = ref('')

const page = ref(1)
const perPage = ref(20)

const actionError = ref('')
const actionMessage = ref('')

const voidTarget = ref<SupplierInvoice | null>(null)
const voiding = ref(false)
const deleteTarget = ref<SupplierInvoice | null>(null)
const deleting = ref(false)

const statuses = SUPPLIER_INVOICE_STATUSES
const today = new Date()

const currencies = computed(() =>
  [...new Set(invoices.value.map((invoice) => invoice.currency_code).filter((code): code is string => Boolean(code)))].sort(),
)

const filtered = computed(() => {
  const term = search.value.trim().toLowerCase()

  return invoices.value.filter((invoice) => {
    if (statusFilter.value && invoice.status !== statusFilter.value) return false
    if (supplierFilter.value && String(invoice.supplier_agent_id) !== supplierFilter.value) return false
    if (currencyFilter.value && (invoice.currency_code ?? '') !== currencyFilter.value) return false
    if (!term) return true

    return `${invoice.invoice_number} ${invoice.supplier_name ?? ''} ${invoice.purchase_order_id ?? ''}`
      .toLowerCase()
      .includes(term)
  })
})

const openCount = computed(() => filtered.value.filter((invoice) => isOpenSupplierInvoice(invoice.status)).length)
const overdueCount = computed(() => filtered.value.filter((invoice) => isOverdueInvoice(invoice, today)).length)

/** Outstanding value per currency, so mixed-currency lists stay readable. */
const valueByCurrency = computed(() => {
  const totals = new Map<string, number>()

  for (const invoice of filtered.value) {
    if (!isOpenSupplierInvoice(invoice.status)) continue

    const code = invoice.currency_code || '—'
    totals.set(code, (totals.get(code) ?? 0) + Number(invoice.invoice_amount || 0))
  }

  return [...totals.entries()]
    .map(([code, total]) => ({ code, total: Number(total.toFixed(2)) }))
    .sort((left, right) => right.total - left.total)
})

const totalPages = computed(() => Math.max(1, Math.ceil(filtered.value.length / perPage.value)))
const paged = computed(() => filtered.value.slice((page.value - 1) * perPage.value, page.value * perPage.value))

watch([search, statusFilter, supplierFilter, currencyFilter, perPage], () => {
  page.value = 1
})

function money(value: number, currency: string | null | undefined): string {
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0))

  return currency ? `${formatted} ${currency}` : formatted
}

function day(value: string | null): string {
  return value ? formatDisplayDate(value) : '—'
}

/** Full invoice body — `PUT` replaces the record, so every field is resent. */
function invoicePayload(invoice: SupplierInvoice, status?: SaveSupplierInvoicePayload['status']): SaveSupplierInvoicePayload {
  return {
    supplier_agent_id: invoice.supplier_agent_id,
    purchase_order_id: invoice.purchase_order_id,
    invoice_number: invoice.invoice_number,
    invoice_date: (invoice.invoice_date ?? '').slice(0, 10),
    due_date: (invoice.due_date ?? '').slice(0, 10),
    currency_code: invoice.currency_code ?? '',
    exchange_rate: invoice.exchange_rate,
    invoice_amount: invoice.invoice_amount,
    department_agent_id: invoice.department_agent_id,
    warehouse_agent_id: invoice.warehouse_agent_id,
    notes: invoice.notes,
    ...(status ? { status } : {}),
  }
}

async function load() {
  loading.value = true
  error.value = ''
  actionError.value = ''
  try {
    const [list, supplierList] = await Promise.all([
      api.listSupplierInvoices(),
      api.listSuppliers().catch(() => []),
    ])
    invoices.value = list
    suppliers.value = supplierList
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

async function confirmVoid() {
  const invoice = voidTarget.value
  if (!invoice) return

  voiding.value = true
  actionError.value = ''
  try {
    const updated = await api.updateSupplierInvoice(invoice.id, invoicePayload(invoice, 'void'))
    const index = invoices.value.findIndex((row) => row.id === invoice.id)
    if (index >= 0) invoices.value[index] = updated
    actionMessage.value = t('procurement.invoice.voidSuccess')
    voidTarget.value = null
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    voiding.value = false
    voidTarget.value = null
  }
}

async function confirmDelete() {
  const invoice = deleteTarget.value
  if (!invoice) return

  deleting.value = true
  actionError.value = ''
  try {
    await api.deleteSupplierInvoice(invoice.id)
    invoices.value = invoices.value.filter((row) => row.id !== invoice.id)
    deleteTarget.value = null
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    deleting.value = false
    deleteTarget.value = null
  }
}

onMounted(load)
</script>

<template>
  <div class="page-heading page-heading--actions">
    <div>
      <h2>{{ t('procurement.invoices.title') }}</h2>
    </div>
    <RouterLink v-if="canCreate" class="profile-action profile-action--primary" to="/supplier-invoices/new">
      <Plus :size="15" aria-hidden="true" />
      {{ t('procurement.invoices.newInvoice') }}
    </RouterLink>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('procurement.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" type="button" @click="load">
      <RefreshCw :size="15" aria-hidden="true" />
      {{ t('procurement.common.retry') }}
    </button>
  </div>

  <template v-else>
    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-label">{{ t('procurement.invoices.count', { count: filtered.length }) }}</span>
        <span class="summary-value">{{ filtered.length }}</span>
      </div>
      <div class="summary-card">
        <span class="summary-label">{{ t('procurement.invoices.open') }}</span>
        <span class="summary-value">{{ openCount }}</span>
      </div>
      <div class="summary-card" :class="{ attention: overdueCount > 0 }">
        <span class="summary-label">{{ t('procurement.invoices.overdue') }}</span>
        <span class="summary-value">{{ overdueCount }}</span>
      </div>
      <div class="summary-card accent">
        <span class="summary-label">{{ t('procurement.invoices.amount') }}</span>
        <span class="summary-value">
          <template v-if="valueByCurrency.length">
            <template v-for="(entry, index) in valueByCurrency" :key="entry.code">
              <template v-if="index > 0"> · </template>{{ money(entry.total, entry.code) }}
            </template>
          </template>
          <template v-else>—</template>
        </span>
      </div>
    </div>

    <div class="toolbar">
      <input v-model="search" class="search-input" type="search" :placeholder="t('procurement.invoices.searchPlaceholder')">
      <select v-model="statusFilter" class="filter-select">
        <option value="">{{ t('procurement.invoices.allStatuses') }}</option>
        <option v-for="status in statuses" :key="status" :value="status">
          {{ t(`procurement.invoiceStatus.${status}`) }}
        </option>
      </select>
      <select v-model="supplierFilter" class="filter-select">
        <option value="">{{ t('procurement.invoices.allSuppliers') }}</option>
        <option v-for="supplier in suppliers" :key="supplier.id" :value="String(supplier.id)">{{ supplier.name }}</option>
      </select>
      <select v-if="currencies.length > 1" v-model="currencyFilter" class="filter-select">
        <option value="">{{ t('procurement.invoices.allCurrencies') }}</option>
        <option v-for="code in currencies" :key="code" :value="code">{{ code }}</option>
      </select>
    </div>

    <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>
    <p v-if="actionError" class="error-inline">{{ actionError }}</p>

    <div v-if="filtered.length === 0" class="panel">
      <span class="empty-inline">{{ t('procurement.invoices.empty') }}</span>
    </div>

    <template v-else>
      <table class="data-table">
        <thead>
          <tr>
            <th>{{ t('procurement.invoices.invoice') }}</th>
            <th>{{ t('procurement.invoices.supplier') }}</th>
            <th>{{ t('procurement.invoices.invoiceDate') }}</th>
            <th>{{ t('procurement.invoices.dueDate') }}</th>
            <th class="cell-qty">{{ t('procurement.invoices.amount') }}</th>
            <th>{{ t('procurement.invoices.status') }}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr v-for="invoice in paged" :key="invoice.id">
            <td class="cell-name">
              {{ invoice.invoice_number }}
              <small class="cell-sub">#{{ invoice.id }}</small>
            </td>
            <td class="cell-name">
              {{ invoice.supplier_name || `#${invoice.supplier_agent_id}` }}
              <small v-if="invoice.purchase_order_id" class="cell-sub">
                {{ t('procurement.invoices.order') }} #{{ invoice.purchase_order_id }}
              </small>
            </td>
            <td>{{ day(invoice.invoice_date) }}</td>
            <td>
              {{ day(invoice.due_date) }}
              <small v-if="isOverdueInvoice(invoice, today)" class="cell-sub overdue-note">
                {{ t('procurement.invoices.overdue') }}
              </small>
            </td>
            <td class="cell-qty">{{ money(invoice.invoice_amount, invoice.currency_code) }}</td>
            <td>
              <span :class="['status-badge', supplierInvoiceStatusClass(invoice.status)]">
                {{ t(`procurement.invoiceStatus.${invoice.status}`) }}
              </span>
            </td>
            <td>
              <div class="cell-actions">
                <RouterLink class="link-action" :to="`/supplier-invoices/${invoice.id}`">
                  {{ t('procurement.orders.open') }}
                  <ArrowRight :size="13" aria-hidden="true" />
                </RouterLink>
                <button
                  v-if="canUpdate && isOpenSupplierInvoice(invoice.status)"
                  class="link-action"
                  type="button"
                  @click="voidTarget = invoice"
                >
                  <Ban :size="13" aria-hidden="true" />
                  {{ t('procurement.invoices.void') }}
                </button>
                <button v-if="canDelete" class="link-action danger" type="button" @click="deleteTarget = invoice">
                  <Trash2 :size="13" aria-hidden="true" />
                  {{ t('procurement.common.delete') }}
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <ListPagination
        v-if="filtered.length > 0"
        :page="page"
        :total-pages="totalPages"
        :total="filtered.length"
        :per-page="perPage"
        @update:page="page = $event"
        @update:per-page="perPage = $event"
      />
    </template>

    <!-- Void dialog -->
    <div v-if="voidTarget" class="modal-overlay" @click.self="voidTarget = null">
      <div class="modal">
        <h3>{{ t('procurement.invoice.voidTitle') }}</h3>
        <p class="form-note">{{ t('procurement.invoice.voidConfirm') }}</p>
        <p class="form-note">{{ voidTarget.invoice_number }} · {{ money(voidTarget.invoice_amount, voidTarget.currency_code) }}</p>
        <div class="modal-actions">
          <button class="btn-secondary" type="button" @click="voidTarget = null">
            <X :size="15" aria-hidden="true" />
            {{ t('procurement.common.cancel') }}
          </button>
          <button class="btn-primary" type="button" :disabled="voiding" @click="confirmVoid">
            <Ban :size="15" aria-hidden="true" />
            {{ voiding ? t('procurement.common.saving') : t('procurement.invoice.void') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete dialog -->
    <div v-if="deleteTarget" class="modal-overlay" @click.self="deleteTarget = null">
      <div class="modal">
        <h3>{{ t('procurement.invoice.deleteTitle') }}</h3>
        <p class="form-note">{{ t('procurement.invoice.deleteConfirm') }}</p>
        <div class="modal-actions">
          <button class="btn-secondary" type="button" @click="deleteTarget = null">
            <X :size="15" aria-hidden="true" />
            {{ t('procurement.common.cancel') }}
          </button>
          <button class="btn-primary" type="button" :disabled="deleting" @click="confirmDelete">
            <Trash2 :size="15" aria-hidden="true" />
            {{ deleting ? t('procurement.common.saving') : t('procurement.common.delete') }}
          </button>
        </div>
      </div>
    </div>
  </template>
</template>

<style scoped>
.overdue-note { color: #a13c2c; }
</style>
